import { useCallback, useState } from 'react';
import { useChainId } from 'src/hooks/chain';
import { useChangesSnackbar } from 'src/hooks/snackbar/useChangesSnackbar';
import { UpdateChainFurnitureInput } from 'src/infrastructure/rest-api/api-types';
import { buildApiUrl, useApiMutation } from 'src/infrastructure/rest-api/useApi';

export const useInventoryMasterDataUpdateChanges = () => {
    const chainId = useChainId();
    const showSnackbar = useChangesSnackbar();
    const [isUpdating, setIsUpdating] = useState(false);

    const [updateFurniture] = useApiMutation('/api/inventorymanagement/chains/{chainId}', 'put', {
        update: (_data, variables, queryClient) => {
            queryClient.invalidateQueries({
                queryKey: [buildApiUrl('/api/inventorymanagement/chains/{chainId}', variables.path)],
            });
        },
    });

    const saveChanges = useCallback(
        async (inputs: UpdateChainFurnitureInput[]) => {
            if (inputs.length === 0) return;
            setIsUpdating(true);

            try {
                const response = await updateFurniture({ path: { chainId }, body: inputs });
                showSnackbar();
                return response;
            } catch (err) {
                showSnackbar(err);
            } finally {
                setIsUpdating(false);
            }
        },
        [updateFurniture, showSnackbar, chainId]
    );

    const updateChanges = useCallback(
        async (input: UpdateChainFurnitureInput) => {
            if (!input?.id) return;
            return saveChanges([input]);
        },
        [saveChanges]
    );

    const reorderChanges = useCallback(
        async (inputs: UpdateChainFurnitureInput[]) => {
            return saveChanges(inputs);
        },
        [saveChanges]
    );

    return [updateChanges, { loading: isUpdating }, reorderChanges] as const;
};
